import React, { useState, useMemo } from 'react';
import {
  X,
  UserCheck,
  Search,
  User,
  Car,
  AlertTriangle,
  Clock,
  CheckCircle
} from 'lucide-react';
import { useFormat } from '../../../i18n/format.js';

function DriverDesignationModal({ fine, vehicle, drivers, onClose, onDesignate, onMessage }) {
  const { t, fmt } = useFormat();
  const [selectedDriverId, setSelectedDriverId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Filter drivers based on search
  const filteredDrivers = useMemo(() => {
    if (!searchQuery.trim()) return drivers;
    const query = searchQuery.toLowerCase();
    return drivers.filter(
      (d) =>
        d.first_name?.toLowerCase().includes(query) ||
        d.last_name?.toLowerCase().includes(query) ||
        d.email?.toLowerCase().includes(query) ||
        d.phone?.includes(query)
    );
  }, [drivers, searchQuery]);

  // Sort drivers - active first, then by name
  const sortedDrivers = useMemo(() => {
    return [...filteredDrivers].sort((a, b) => {
      // Active drivers first
      if (a.status === 'active' && b.status !== 'active') return -1;
      if (a.status !== 'active' && b.status === 'active') return 1;
      // Then by last name
      return (a.last_name || '').localeCompare(b.last_name || '');
    });
  }, [filteredDrivers]);

  const handleSubmit = async () => {
    if (!selectedDriverId) {
      onMessage?.({
        title: t('fines.messages.errorTitle'),
        message: t('fines.errors.driverRequired'),
        type: 'error',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await onDesignate(fine.id, selectedDriverId);
      onClose();
    } catch (error) {
      onMessage?.({
        title: t('fines.messages.errorTitle'),
        message: error.message || t('fines.errors.designationFailed'),
        type: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-orange-50">
          <div className="flex items-center gap-3">
            <UserCheck className="w-6 h-6 text-orange-500" />
            <div>
              <h2 className="text-lg font-semibold">{t('fines.designation.title')}</h2>
              <p className="text-sm text-gray-600">
                {t('fines.designation.subtitle', { reference: fine.antai_reference || `#${fine.id}` })}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Deadline Warning */}
        {fine.days_remaining !== undefined && fine.days_remaining <= 10 && (
          <div
            className={`p-3 flex items-center gap-2 ${
              fine.days_remaining <= 0
                ? 'bg-red-100 text-red-800'
                : fine.days_remaining <= 3
                ? 'bg-orange-100 text-orange-800'
                : 'bg-yellow-100 text-yellow-800'
            }`}
          >
            <AlertTriangle className="w-5 h-5" />
            <span className="font-medium">
              {fine.days_remaining <= 0
                ? t('fines.detail.deadlineOverdue', { count: Math.abs(fine.days_remaining) })
                : t('fines.designation.remaining', { count: fine.days_remaining })}
            </span>
            <Clock className="w-4 h-4 ml-auto" />
            <span className="text-sm">
              {t('fines.designation.deadline', { date: fmt.date(fine.designation_deadline) })}
            </span>
          </div>
        )}

        {/* Vehicle Info */}
        {vehicle && (
          <div className="p-3 bg-gray-50 border-b flex items-center gap-3">
            <Car className="w-5 h-5 text-gray-500" />
            <span className="font-medium">{vehicle.license_plate}</span>
            <span className="text-gray-500">
              {vehicle.make} {vehicle.model}
            </span>
          </div>
        )}

        {/* Search */}
        <div className="p-4 border-b">
          <div className="relative">
            <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('fines.designation.searchPlaceholder')}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              autoFocus
            />
          </div>
        </div>

        {/* Driver List */}
        <div className="flex-1 overflow-y-auto p-2">
          {sortedDrivers.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <User className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>{t('fines.designation.noDriverFound')}</p>
            </div>
          ) : (
            <div className="space-y-1">
              {sortedDrivers.map((driver) => (
                <button
                  key={driver.id}
                  onClick={() => setSelectedDriverId(driver.id)}
                  className={`w-full p-3 rounded-lg flex items-center gap-3 transition-colors ${
                    selectedDriverId === driver.id
                      ? 'bg-orange-100 border-2 border-orange-500'
                      : 'hover:bg-gray-100 border-2 border-transparent'
                  }`}
                >
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      driver.status === 'active'
                        ? 'bg-green-100 text-green-600'
                        : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    <User className="w-5 h-5" />
                  </div>
                  <div className="flex-1 text-left">
                    <div className="font-medium">
                      {driver.first_name} {driver.last_name}
                    </div>
                    <div className="text-sm text-gray-500">{driver.email}</div>
                  </div>
                  {driver.status === 'active' ? (
                    <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">
                      {t('fines.designation.driverActive')}
                    </span>
                  ) : (
                    <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">
                      {driver.status}
                    </span>
                  )}
                  {selectedDriverId === driver.id && (
                    <CheckCircle className="w-5 h-5 text-orange-500" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex justify-between items-center">
          <div className="text-sm text-gray-500">
            {t('fines.designation.driverCount', { count: sortedDrivers.length })}
            {selectedDriverId && (
              <span className="ml-2 text-orange-600 font-medium">{t('fines.designation.selectedOne')}</span>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border rounded-lg hover:bg-gray-100"
              disabled={isSubmitting}
            >
              {t('common.actions.cancel')}
            </button>
            <button
              onClick={handleSubmit}
              className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 flex items-center gap-2"
              disabled={!selectedDriverId || isSubmitting}
            >
              <UserCheck className="w-4 h-4" />
              {isSubmitting ? t('fines.designation.submitting') : t('fines.designation.submit')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DriverDesignationModal;
